import React, { useEffect, useRef } from 'react';
import { View, ViewPropTypes, Platform, Dimensions } from 'react-native'


import { get as _get } from 'lodash';
import PropTypes from 'prop-types';

import FrameworkTestKeys from '../../../../FrameworkTestKeys';
import { PositionsProvider, TooltipsIds } from '../../../../Services/PositionsProvider';
import { COMPONENT_NAMES } from '../../../../Utils';
import DashboardMainCard from '../DashboardMainCard';

import styles from './DashboardMainCards.Styles.js';

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'
import { Card } from '@vfgroup-oneplatform/foundation/Components';

const cardPropTypes = {
  component: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.func
  ]),
  extraInfo: PropTypes.object,
  error: PropTypes.bool,
  loadingConfig: PropTypes.array,
  isVerticalLayout: PropTypes.bool,
  isHorizontalLayout: PropTypes.bool,
  onPressCard: PropTypes.func,
  hasShadow: PropTypes.bool,
  accessibilityLabel: PropTypes.string,
  cardAccessible: PropTypes.bool
}

const CardWrapper = ({ Card, type, hasShadow, isLoading, shimmerPlaceholderConfig, isFourthCard = false }) => {
  const {
    extraInfo = {},
    error,
    loadingConfig,
    isVerticalLayout,
    isHorizontalLayout,
    onPressCard,
    style,
    accessibilityLabel,
    cardAccessible,
    ...otherInfo
  } = Card;


  return (
    <DashboardMainCard
      {...otherInfo}
      extraInfo={extraInfo}
      type={type}
      accessibilityLabel={accessibilityLabel}
      componentName={Card ? Card.name : null}
      showAction={!Card}
      style={style}
      onPressCard={onPressCard}
      hasShadow={hasShadow}
      loadingConfig={loadingConfig}
      shimmerPlaceholderConfig={shimmerPlaceholderConfig}
      isLoading={isLoading}
      cardAccessible={cardAccessible}
      isFourthCard={isFourthCard}
      error={!!error}
      errorCardProps={{
        accessibilityID: accessibilityLabel,
        errorText: error?.errorText,
        errorHandler: error?.errorHandler,
        isVerticalLayout: isVerticalLayout,
        isHorizontalLayout: isHorizontalLayout,
      }}
    >
      <Card.component {...Card} />
    </DashboardMainCard>
  );
};

CardWrapper.propTypes = {
  hasShadow: PropTypes.bool,
  shimmerPlaceholderConfig: PropTypes.object,
  isLoading: PropTypes.bool,
  Card: PropTypes.shape(cardPropTypes),
  type: PropTypes.oneOf(['main', 'sec', 'addMore']),
};

const onMainCardsLayout = mainCardRef => {
  PositionsProvider.measure({
    id: TooltipsIds.MAIN_CARDS,
    ref: mainCardRef.current,
    isAbsolute: false,
  });
};
function DashboardMainCards({
  Card1,
  Card2,
  Card3,
  Card4,
  hasShadow,
  cardsLoading,
  shimmerPlaceholderConfig,
  dashboardMainCardsContainerStyle,
}) {


  const { isLoadingMainCard, isLoadingSecondaryCard, isLoadingThirdCard, isLoadingFourthCard } =
    cardsLoading;
  const mainCardRef = useRef();
  useEffect(() => {
    onMainCardsLayout(mainCardRef);
  });
  const theme = useTheme()

  const EmptyComponent = () => (
    <View style={{
      height: Platform.OS === 'web' && Dimensions.get('window').width > 700 ? 200 : 100,
      backgroundColor: theme.colors.cardBackgroundColor,
      borderRadius: 6,
      elevation: 1,
      shadowColor: 'rgb(0, 0, 0)',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowRadius: 8,
      shadowOpacity: 0.16,
      marginHorizontal: 15
    }} />
  )

  if (Platform.OS === 'web' && Dimensions.get('window').width > 700) {
    return (
      <View style={[styles.desktopContainerStyle(theme), dashboardMainCardsContainerStyle, { paddingHorizontal: 5, paddingTop: 30 }]}>
        {_get(Card2, 'name') === COMPONENT_NAMES.SECONDARY && (
          <CardWrapper
            Card={Card2}
            type="main"
            isLoading={isLoadingSecondaryCard}
            shimmerPlaceholderConfig={shimmerPlaceholderConfig}
            hasShadow={hasShadow}
            style={{ maxHeight: '350px' }}
          />
        )}
        <View style={[styles.secondaryCardsContainerStyle]}>
          {(_get(Card1, 'name') === COMPONENT_NAMES.MAIN) ?
            <CardWrapper
              Card={Card1}
              type="sec"
              isLoading={isLoadingMainCard}
              shimmerPlaceholderConfig={shimmerPlaceholderConfig}
              hasShadow={hasShadow}
            />
            : <EmptyComponent />}
          <View style={[styles.addMoreContainersStyle, { flexDirection: 'row', marginTop: 20, marginLeft: 0 }]}>
            {_get(Card4, 'name') && (
              <CardWrapper
                Card={Card4}
                type="addMore"
                isLoading={isLoadingFourthCard}
                shimmerPlaceholderConfig={shimmerPlaceholderConfig}
                hasShadow={hasShadow}
              />
            )}
            {_get(Card3, 'name') && (
              <CardWrapper
                Card={Card3}
                type="addMore"
                isLoading={isLoadingThirdCard}
                shimmerPlaceholderConfig={shimmerPlaceholderConfig}
                hasShadow={hasShadow}
                isFourthCard={true}
              />
            )}
          </View>
        </View>
      </View>
    )
  }


  return (
    <View style={[styles.containerStyle(theme), dashboardMainCardsContainerStyle]}>
      {_get(Card1, 'name') === COMPONENT_NAMES.MAIN ?
        <View ref={mainCardRef} style={[styles.mainCardStyle]} testID={FrameworkTestKeys.MainCard}>
          <CardWrapper
            Card={Card1}
            type="main"
            isLoading={isLoadingMainCard}
            shimmerPlaceholderConfig={shimmerPlaceholderConfig}
            hasShadow={hasShadow}
          />
        </View>
        : <EmptyComponent />}
      <View
        style={styles.secondaryCardsContainerStyle}
        testID={FrameworkTestKeys.dashboardMainCardsSecondaryView}
      >
        {_get(Card2, 'name') === COMPONENT_NAMES.SECONDARY && (
          <CardWrapper
            Card={Card2}
            type="sec"
            isLoading={isLoadingSecondaryCard}
            shimmerPlaceholderConfig={shimmerPlaceholderConfig}
            hasShadow={hasShadow}
          />
        )}
        <View
          style={styles.addMoreContainersStyle}
          testID={FrameworkTestKeys.dashboardMainCardsAddMoreView}
        >
          {_get(Card3, 'name') && (
            <CardWrapper
              Card={Card3}
              type="addMore"
              isLoading={isLoadingThirdCard}
              shimmerPlaceholderConfig={shimmerPlaceholderConfig}
              hasShadow={hasShadow}
            />
          )}
          {_get(Card4, 'name') && (
            <View
              style={styles.fourthComponentCard}
              testID={FrameworkTestKeys.dashboardMainCardsFourthView}
            >
              <CardWrapper
                Card={Card4}
                type="addMore"
                isLoading={isLoadingFourthCard}
                shimmerPlaceholderConfig={shimmerPlaceholderConfig}
                hasShadow={hasShadow}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );
}



DashboardMainCards.propTypes = {
  hasShadow: PropTypes.bool,
  shimmerPlaceholderConfig: PropTypes.object,
  cardsLoading: PropTypes.shape({
    isLoadingMainCard: PropTypes.bool,
    isLoadingSecondaryCard: PropTypes.bool,
    isLoadingThirdCard: PropTypes.bool,
    isLoadingFourthCard: PropTypes.bool,
  }),
  Card1: PropTypes.shape(cardPropTypes),
  Card2: PropTypes.shape(cardPropTypes),
  Card3: PropTypes.shape(cardPropTypes),
  Card4: PropTypes.shape(cardPropTypes),
  dashboardMainCardsContainerStyle: ViewPropTypes.style,
};

export default DashboardMainCards;
